import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiX, FiToggleLeft, FiToggleRight, FiChevronDown, FiChevronRight, FiCheck, FiArrowUp, FiArrowDown, FiChevronsUp, FiChevronsDown } from 'react-icons/fi';
import { showDelete, showAlert } from '../utils/dialog';
import { adminApi } from '../adminApi';
import MultiImageUpload from '../components/MultiImageUpload';

const EMPTY = { name:'', description:'', price:'', discount_price:'', stock:'', category_id:'', image_url:'', image_url_2:'', image_url_3:'', is_active: true, barcode:'' };
const EMPTY_VARIANTS = [];  // list of variant product IDs

function buildTree(cats, parentId = null) {
  return cats
    .filter(c => (c.parent_id ?? null) === parentId)
    .map(c => ({ ...c, children: buildTree(cats, c.id) }));
}

function flattenForSelect(nodes, depth = 0) {
  return nodes.flatMap(n => [
    { id: n.id, label: (depth > 0 ? '  '.repeat(depth) + '› ' : '') + n.name },
    ...flattenForSelect(n.children, depth + 1),
  ]);
}

function CategoryPicker({ categories, value, onChange }) {
  const [open, setOpen]       = useState(false);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch]   = useState('');
  const ref     = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open) { setSearch(''); setTimeout(() => inputRef.current?.focus(), 50); }
  }, [open]);

  const tree = buildTree(categories);

  const labelFor = (id) => {
    if (!id) return '';
    const chain = [];
    let c = categories.find(c => c.id === Number(id));
    while (c) { chain.unshift(c.name); c = c.parent_id ? categories.find(p => p.id === c.parent_id) : null; }
    return chain.join(' › ');
  };

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  const select = (id) => { onChange(String(id)); setOpen(false); setSearch(''); };

  const flatAll = flattenForSelect(tree);
  const filtered = search.trim()
    ? categories.filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
    : null;

  function renderNodes(nodes, depth = 0) {
    return nodes.map(node => {
      const isLeaf = node.children.length === 0;
      return (
        <div key={node.id}>
          <div
            className={`cat-picker-item ${Number(value) === node.id ? 'selected' : ''} ${!isLeaf ? 'has-children' : ''}`}
            style={{ paddingLeft: 12 + depth * 16 }}
            onClick={() => isLeaf ? select(node.id) : toggle(node.id)}
          >
            <span className="cat-picker-arrow">
              {!isLeaf
                ? (expanded[node.id] ? <FiChevronDown size={12}/> : <FiChevronRight size={12}/>)
                : <span style={{ width: 12, display: 'inline-block' }} />
              }
            </span>
            {node.name}
          </div>
          {!isLeaf && expanded[node.id] && renderNodes(node.children, depth + 1)}
        </div>
      );
    });
  }

  return (
    <div className="cat-picker-wrap" ref={ref}>
      <button type="button" className="cat-picker-trigger" onClick={() => setOpen(o => !o)}>
        <span className={value ? '' : 'placeholder'}>{value ? labelFor(value) : 'Select category…'}</span>
        <FiChevronDown size={14} className={`cat-picker-chevron ${open ? 'open' : ''}`} />
      </button>
      {open && (
        <div className="cat-picker-dropdown">
          <div className="cat-picker-search-wrap">
            <FiSearch size={13} className="cat-picker-search-icon" />
            <input
              ref={inputRef}
              className="cat-picker-search"
              placeholder="Search category…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
            {search && (
              <button className="cat-picker-search-clear" onClick={() => setSearch('')} type="button">
                <FiX size={12} />
              </button>
            )}
          </div>
          {filtered
            ? filtered.length === 0
              ? <div className="cat-picker-no-results">No categories found</div>
              : filtered.map(cat => (
                  <div
                    key={cat.id}
                    className={`cat-picker-item ${Number(value) === cat.id ? 'selected' : ''}`}
                    style={{ paddingLeft: 12 }}
                    onClick={() => select(cat.id)}
                  >
                    <span style={{ width: 12, display: 'inline-block' }} />
                    {cat.name}
                  </div>
                ))
            : renderNodes(tree)
          }
        </div>
      )}
    </div>
  );
}

function AdminProducts() {
  const [products,   setProducts]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [filter,     setFilter]     = useState('all');
  const [modal,          setModal]          = useState(false);
  const [editing,        setEditing]        = useState(null);
  const [form,           setForm]           = useState(EMPTY);
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState('');
  const [variantIds,     setVariantIds]     = useState(EMPTY_VARIANTS);
  const [variantSearch,  setVariantSearch]  = useState('');
  const [orderChanged, setOrderChanged] = useState(false);
  const [savingOrder,  setSavingOrder]  = useState(false);
  const [overIdx,    setOverIdx]    = useState(null);
  const [multiSelected, setMultiSelected] = useState(new Set());
  const [discountEdits, setDiscountEdits] = useState({});
  const [discountOpen, setDiscountOpen]   = useState(new Set());
  const [expandedVariant, setExpandedVariant] = useState(null);
  const dragIdx = useRef(null);
  const errorRef = useRef(null);

  const load = () => {
    setLoading(true);
    Promise.all([adminApi.getProducts(), adminApi.getCategories()])
      .then(([p, c]) => {
        setProducts(p.data);
        setCategories(c.data);
        setDiscountOpen(new Set((p.data || []).filter(x => x.discount_price && x.discount_active !== false).map(x => x.id)));
        setDiscountEdits({});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const onDragStart = (idx) => { dragIdx.current = idx; };
  const onDragOver  = (e, idx) => { e.preventDefault(); setOverIdx(idx); };
  const onDrop      = (idx) => {
    if (dragIdx.current === null || dragIdx.current === idx) { setOverIdx(null); return; }
    const a = [...products];
    const [moved] = a.splice(dragIdx.current, 1);
    a.splice(idx, 0, moved);
    setProducts(a);
    setOrderChanged(true);
    dragIdx.current = null;
    setOverIdx(null);
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await adminApi.reorderProducts(products.map(p => p.id));
      setOrderChanged(false);
    } catch { showAlert('Failed to save order'); }
    finally { setSavingOrder(false); }
  };

  const toggleMulti = (id) => {
    setMultiSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const toggleMultiAll = () => {
    if (multiSelected.size === filtered.length) {
      setMultiSelected(new Set());
    } else {
      setMultiSelected(new Set(filtered.map(p => p.id)));
    }
  };

  const moveGroup = (dir) => {
    if (multiSelected.size === 0) return;
    const arr = [...products];
    const selIdxs = arr.map((p, i) => multiSelected.has(p.id) ? i : -1).filter(i => i >= 0);
    if (dir === 'top') {
      setProducts([...arr.filter(p => multiSelected.has(p.id)), ...arr.filter(p => !multiSelected.has(p.id))]);
    } else if (dir === 'bottom') {
      setProducts([...arr.filter(p => !multiSelected.has(p.id)), ...arr.filter(p => multiSelected.has(p.id))]);
    } else if (dir === 'up') {
      if (selIdxs[0] === 0) return;
      const next = [...arr];
      selIdxs.forEach(i => { [next[i - 1], next[i]] = [next[i], next[i - 1]]; });
      setProducts(next);
    } else if (dir === 'down') {
      if (selIdxs[selIdxs.length - 1] === arr.length - 1) return;
      const next = [...arr];
      [...selIdxs].reverse().forEach(i => { [next[i + 1], next[i]] = [next[i], next[i + 1]]; });
      setProducts(next);
    }
    setOrderChanged(true);
  };

  const openAdd  = () => { setEditing(null); setForm(EMPTY); setError(''); setVariantIds([]); setVariantSearch(''); setModal(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ name:p.name, description:p.description, price:p.price, discount_price:p.discount_price||'', stock:p.stock, category_id:p.category_id, image_url:p.image_url, image_url_2:p.image_url_2||'', image_url_3:p.image_url_3||'', is_active: p.is_active !== false, barcode:p.barcode||'' });
    setVariantIds([]); setVariantSearch('');
    adminApi.getProductVariants(p.id).then(r => setVariantIds((r.data || []).map(v => v.id))).catch(() => {});
    setError(''); setModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const showErr = (msg) => { setError(msg); setTimeout(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50); };
    if (!form.name.trim())        return showErr('Product name is required');
    if (!form.description.trim()) return showErr('Description is required');
    if (!form.price)              return showErr('Price is required');
    if (form.stock === '')        return showErr('Stock is required');
    if (!form.category_id)        return showErr('Category is required');
    if (!form.image_url)          return showErr('Product image is required');
    setError(''); setSaving(true);
    try {
      const payload = { ...form, price:parseFloat(form.price), discount_price:form.discount_price?parseFloat(form.discount_price):null, stock:parseInt(form.stock), category_id:parseInt(form.category_id) };
      const res = editing ? await adminApi.updateProduct(editing, payload) : await adminApi.createProduct(payload);
      const savedId = res.data?.id || editing;
      if (savedId) await adminApi.setProductVariants(savedId, variantIds).catch(() => {});
      setModal(false); load();
    } catch (err) { showErr(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!await showDelete('Delete this product?')) return;
    await adminApi.deleteProduct(id).catch(() => {}); load();
  };

  const handleToggle = (id) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, is_active: p.is_active === false ? true : false } : p));
    adminApi.toggleProductActive(id).catch(() => {
      setProducts(prev => prev.map(p => p.id === id ? { ...p, is_active: p.is_active === false ? true : false } : p));
    });
  };

  const toggleDiscount = (id) => {
    const p = products.find(x => x.id === id);
    if (!p) return;
    const isOn = discountOpen.has(id);
    setDiscountOpen(prev => {
      const n = new Set(prev);
      isOn ? n.delete(id) : n.add(id);
      return n;
    });
    setDiscountEdits(prev => { const n = { ...prev }; delete n[id]; return n; });
    if (isOn) {
      setProducts(prev => prev.map(x => x.id === id ? { ...x, discount_active: false } : x));
      adminApi.updateProduct(id, { discount_active: false }).catch(() => load());
    } else {
      setProducts(prev => prev.map(x => x.id === id ? { ...x, discount_active: true } : x));
      adminApi.updateProduct(id, { discount_active: true }).catch(() => load());
    }
  };

  const saveDiscountPrice = async (id) => {
    const p = products.find(x => x.id === id);
    if (!p) return;
    const val = discountEdits[id];
    if (val === undefined || val === '') {
      setDiscountEdits(prev => { const n = { ...prev }; delete n[id]; return n; });
      return;
    }
    const discount_price = parseFloat(val);
    if (isNaN(discount_price) || discount_price <= 0 || discount_price >= p.price) {
      setDiscountEdits(prev => { const n = { ...prev }; delete n[id]; return n; });
      return;
    }
    setProducts(prev => prev.map(x => x.id === id ? { ...x, discount_price, discount_active: true } : x));
    setDiscountEdits(prev => { const n = { ...prev }; delete n[id]; return n; });
    setDiscountOpen(prev => { const n = new Set(prev); n.add(id); return n; });
    adminApi.updateProduct(id, { discount_price, discount_active: true }).catch(() => load());
  };

  // Build flat category list with depth-based indentation
  const categoryOptions = flattenForSelect(buildTree(categories));

  const catName = (catId) => {
    const chain = [];
    let c = categories.find(c => c.id === catId);
    while (c) {
      chain.unshift(c.name);
      c = c.parent_id ? categories.find(p => p.id === c.parent_id) : null;
    }
    return chain.length ? chain.join(' › ') : '—';
  };

  const filtered = products.filter(p => {
    const q = search.toLowerCase().trim();
    const matchQ = !q || p.name.toLowerCase().includes(q) || (p.barcode || '').toLowerCase().includes(q);
    const matchF = filter === 'all' || (filter === 'active' && p.is_active !== false) || (filter === 'inactive' && p.is_active === false);
    return matchQ && matchF;
  });

  const activeCount   = products.filter(p => p.is_active !== false).length;
  const inactiveCount = products.filter(p => p.is_active === false).length;

  return (
    <div>
      <div className="admin-card">
        <div className="admin-card-header">
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <h3 style={{ margin:0 }}>Products ({products.length})</h3>
            <div className="prod-filter-tabs">
              {[['all','All'], ['active','Active'], ['inactive','Inactive']].map(([val, lbl]) => (
                <button key={val} className={`prod-filter-tab ${filter===val?'active':''}`} onClick={() => setFilter(val)}>
                  {lbl}
                  <span className="prod-filter-count">
                    {val==='all' ? products.length : val==='active' ? activeCount : inactiveCount}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div style={{ display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap' }}>
            {multiSelected.size > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{multiSelected.size} selected</span>
                <button onClick={() => moveGroup('top')} style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 8px', border: '1.5px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '.72rem', fontWeight: 600 }}>
                  <FiChevronsUp size={13} /> Top
                </button>
                <button onClick={() => moveGroup('up')} style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 8px', border: '1.5px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '.72rem', fontWeight: 600 }}>
                  <FiArrowUp size={13} /> Up
                </button>
                <button onClick={() => moveGroup('down')} style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 8px', border: '1.5px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '.72rem', fontWeight: 600 }}>
                  <FiArrowDown size={13} /> Down
                </button>
                <button onClick={() => moveGroup('bottom')} style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 8px', border: '1.5px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '.72rem', fontWeight: 600 }}>
                  <FiChevronsDown size={13} /> Bottom
                </button>
              </div>
            )}
            <div className="admin-search">
              <FiSearch size={14} color="var(--text-muted)" />
              <input placeholder="Search by name or barcode…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            {orderChanged && (
              <button className="btn-admin btn-admin-primary" onClick={saveOrder} disabled={savingOrder}>
                {savingOrder ? 'Saving…' : '💾 Save Order'}
              </button>
            )}
            <button className="btn-admin btn-admin-primary" onClick={openAdd}><FiPlus size={15} /> Add Product</button>
          </div>
        </div>

        <div className="admin-table-wrap">
          {loading
            ? <div className="admin-loading"><div className="admin-spinner" />Loading…</div>
            : filtered.length === 0
              ? <div className="admin-empty">No products found</div>
              : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th style={{width:32}}>
                        <div onClick={toggleMultiAll} style={{ width:18, height:18, borderRadius:4, cursor:'pointer', border: multiSelected.size === filtered.length && filtered.length > 0 ? '2px solid #6366f1' : '2px solid #cbd5e1', background: multiSelected.size === filtered.length && filtered.length > 0 ? '#6366f1' : 'transparent', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto' }}>
                          {multiSelected.size === filtered.length && filtered.length > 0 && <FiCheck size={11} color="#fff" strokeWidth={3} />}
                        </div>
                      </th>
                      <th style={{width:32}}></th><th>Product</th><th>Barcode / Article</th><th>Category</th><th>Price</th><th>Stock</th><th>Status</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((p, idx) => {
                      const isMultiSel = multiSelected.has(p.id);
                      return (<React.Fragment key={p.id}>
                      <tr
                        draggable={!search}
                        onDragStart={() => onDragStart(idx)}
                        onDragOver={e => onDragOver(e, idx)}
                        onDrop={() => onDrop(idx)}
                        onDragEnd={() => setOverIdx(null)}
                        style={{
                          opacity: p.is_active === false ? .55 : 1,
                          background: isMultiSel ? '#eef2ff' : overIdx === idx ? '#eff6ff' : 'transparent',
                          transition: 'background .1s',
                        }}
                      >
                        <td onClick={() => toggleMulti(p.id)} style={{ cursor: 'pointer', textAlign: 'center' }}>
                          <div style={{ width:18, height:18, borderRadius:4, border: isMultiSel ? '2px solid #6366f1' : '2px solid #cbd5e1', background: isMultiSel ? '#6366f1' : 'transparent', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto' }}>
                            {isMultiSel && <FiCheck size={11} color="#fff" strokeWidth={3} />}
                          </div>
                        </td>
                        <td style={{ cursor: search ? 'default' : 'grab', color: '#cbd5e1', fontSize: '1.1rem', userSelect: 'none', textAlign: 'center' }}>
                          {!search && '⠿'}
                        </td>
                        <td
                          onClick={() => setExpandedVariant(prev => prev === p.id ? null : p.id)}
                          style={{ cursor: 'pointer' }}
                          title="Click to see variants"
                        >
                          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                            <img src={p.image_url || 'https://placehold.co/40x40'} alt={p.name} className="product-thumb" />
                            <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                              <span className="bold">{p.name}</span>
                              {p.variant_group_id && (
                                <span style={{ fontSize:'.68rem', color:'#6366f1', fontWeight:600 }}>
                                  {expandedVariant === p.id ? '▲ Hide variants' : '▼ Show variants'}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td style={{ fontSize:'.8125rem', fontFamily:'monospace', color: p.barcode ? 'var(--text)' : 'var(--text-muted)' }}>
                          {p.barcode || '—'}
                        </td>
                        <td style={{ fontSize:'.8125rem' }}>{catName(p.category_id)}</td>
                        <td style={{ minWidth: 160 }}>
                          <div style={{ display:'flex', flexDirection:'column', gap: 4 }}>
                            <span className="bold" style={{ fontSize:'.8rem', color:'#64748b' }}>Rs {p.price.toFixed(0)}</span>
                            <div style={{ display:'flex', alignItems:'center', gap: 5, width: 150 }}>
                              <button
                                onClick={() => toggleDiscount(p.id)}
                                style={{
                                  fontSize:'.65rem', fontWeight:700, padding:'1px 0', borderRadius:4, cursor:'pointer',
                                  width: 36, textAlign: 'center', flexShrink: 0,
                                  background: discountOpen.has(p.id) ? '#dcfce7' : '#f1f5f9',
                                  color: discountOpen.has(p.id) ? '#16a34a' : '#94a3b8',
                                  border: `1px solid ${discountOpen.has(p.id) ? '#86efac' : '#e2e8f0'}`,
                                }}
                              >
                                {discountOpen.has(p.id) ? 'ON' : 'OFF'}
                              </button>
                              {discountOpen.has(p.id) ? (
                                <input
                                  type="number"
                                  min="0"
                                  value={discountEdits[p.id] !== undefined ? discountEdits[p.id] : (p.discount_price?.toFixed(0) || '')}
                                  onChange={e => setDiscountEdits(prev => ({ ...prev, [p.id]: e.target.value }))}
                                  onBlur={() => saveDiscountPrice(p.id)}
                                  onKeyDown={e => { if (e.key === 'Enter') { e.target.blur(); } e.stopPropagation(); }}
                                  onClick={e => e.stopPropagation()}
                                  placeholder="—"
                                  style={{ width:72, padding:'2px 6px', border:'1.5px solid #86efac', borderRadius:5, fontSize:'.8rem', fontFamily:'inherit', outline:'none', color:'#16a34a', fontWeight:700, flexShrink: 0 }}
                                />
                              ) : p.discount_price ? (
                                <span style={{ fontSize:'.8rem', color:'#94a3b8', fontWeight:600 }}>Rs {p.discount_price.toFixed(0)}</span>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        <td><span className={`badge ${p.stock>10?'badge-success':p.stock>0?'badge-warning':'badge-error'}`}>{p.stock}</span></td>
                        <td>
                          <button className={`prod-status-btn ${p.is_active!==false?'prod-status-active':'prod-status-inactive'}`} onClick={() => handleToggle(p.id)} title="Click to toggle">
                            {p.is_active !== false
                              ? <><FiToggleRight size={15}/> Active</>
                              : <><FiToggleLeft size={15}/> Inactive</>
                            }
                          </button>
                        </td>
                        <td>
                          <div style={{ display:'flex', gap:6 }}>
                            <button className="btn-admin btn-admin-ghost btn-admin-sm" onClick={() => openEdit(p)}><FiEdit2 size={13}/></button>
                            <button className="btn-admin btn-admin-danger btn-admin-sm" onClick={() => handleDelete(p.id)}><FiTrash2 size={13}/></button>
                          </div>
                        </td>
                      </tr>
                      {/* Variants sub-row */}
                      {expandedVariant === p.id && p.variant_group_id && (() => {
                        const variants = products.filter(v => v.variant_group_id === p.variant_group_id && v.id !== p.id);
                        if (variants.length === 0) return (
                          <tr key={`v-empty-${p.id}`}>
                            <td colSpan={9} style={{ background:'#f8faff', padding:'10px 20px', fontSize:'.8rem', color:'#94a3b8', borderBottom:'2px solid #e2e8f0' }}>
                              No other variants linked yet. Edit this product to add variants.
                            </td>
                          </tr>
                        );
                        return (
                          <tr key={`v-${p.id}`}>
                            <td colSpan={9} style={{ background:'#f5f3ff', padding:'10px 16px', borderBottom:'2px solid #e2e8f0' }}>
                              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                                <span style={{ fontSize:'.72rem', fontWeight:700, color:'#6366f1', marginRight:4 }}>Variants:</span>
                                {variants.map(v => (
                                  <div key={v.id} style={{ display:'flex', alignItems:'center', gap:7, background:'#fff', border:'1.5px solid #e0e7ff', borderRadius:8, padding:'5px 10px' }}>
                                    <img src={v.image_url||'https://placehold.co/28x28'} alt={v.name} style={{ width:28, height:28, objectFit:'contain', borderRadius:4, border:'1px solid #e2e8f0' }}/>
                                    <span style={{ fontSize:'.78rem', fontWeight:600, color:'#1e293b' }}>{v.name}</span>
                                    <span style={{ fontSize:'.72rem', color:'#94a3b8' }}>Rs {(v.discount_price||v.price).toFixed(0)}</span>
                                    <span className={`badge ${v.stock>10?'badge-success':v.stock>0?'badge-warning':'badge-error'}`} style={{ fontSize:'.65rem' }}>{v.stock}</span>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        );
                      })()}
                    </React.Fragment>);})}
                  </tbody>
                </table>
              )
          }
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h3>{editing ? 'Edit Product' : 'Add Product'}</h3>
              <button className="modal-close" onClick={() => setModal(false)}><FiX size={16}/></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                {error && <div ref={errorRef} className="admin-error" style={{ marginBottom: 4 }}>{error}</div>}
                <div className="admin-form-group"><label>Product Name *</label><input value={form.name} onChange={e=>{setForm({...form,name:e.target.value});setError('');}} placeholder="e.g. iPhone 15"/></div>
                <div className="admin-form-group"><label>Description *</label><textarea value={form.description} onChange={e=>{setForm({...form,description:e.target.value});setError('');}} rows={2}/></div>
                <div className="admin-form-row">
                  <div className="admin-form-group"><label>Price (Rs) *</label><input type="number" step="0.01" min="0" value={form.price} onChange={e=>{setForm({...form,price:e.target.value});setError('');}}/></div>
                  <div className="admin-form-group"><label>Discount Price (Rs)</label><input type="number" step="0.01" min="0" value={form.discount_price} onChange={e=>setForm({...form,discount_price:e.target.value})}/></div>
                </div>
                <div className="admin-form-row">
                  <div className="admin-form-group"><label>Stock *</label><input type="number" min="0" value={form.stock} onChange={e=>{setForm({...form,stock:e.target.value});setError('');}}/></div>
                  <div className="admin-form-group">
                    <label>Category *</label>
                    <CategoryPicker
                      categories={categories}
                      value={form.category_id}
                      onChange={v => setForm({...form, category_id: v})}
                    />
                  </div>
                </div>
                <div className="admin-form-group"><label>Barcode / Article Number</label><input value={form.barcode} onChange={e=>setForm({...form,barcode:e.target.value})} placeholder="Scan or type barcode / article #" style={{ fontFamily:'monospace' }}/></div>
                <MultiImageUpload
                  values={[form.image_url, form.image_url_2, form.image_url_3].filter(Boolean)}
                  onChange={imgs => setForm({ ...form, image_url: imgs[0] || '', image_url_2: imgs[1] || '', image_url_3: imgs[2] || '' })}
                />
                {/* ── Variants ── */}
                <div className="admin-form-group">
                  <label>Variants (other products that are variants of this one)</label>
                  <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                    <div className="admin-search" style={{ flex:1 }}>
                      <FiSearch size={13} color="var(--text-muted)" />
                      <input
                        placeholder="Search product to add as variant…"
                        value={variantSearch}
                        onChange={e => setVariantSearch(e.target.value)}
                      />
                      {variantSearch && <button onClick={() => setVariantSearch('')} style={{ background:'none', border:'none', cursor:'pointer', padding:2, display:'flex', color:'var(--text-muted)' }}><FiX size={12}/></button>}
                    </div>
                  </div>
                  {/* All products list — filtered by search */}
                  {(() => {
                    const q = variantSearch.toLowerCase().trim();
                    const results = products.filter(p =>
                      p.id !== editing &&
                      !variantIds.includes(p.id) &&
                      (!q || p.name.toLowerCase().includes(q) || (p.barcode||'').toLowerCase().includes(q))
                    );
                    return results.length > 0 ? (
                      <div style={{ border:'1.5px solid #e2e8f0', borderRadius:8, overflow:'hidden', marginBottom:8, maxHeight:280, overflowY:'auto' }}>
                        {results.map(p => (
                          <div key={p.id}
                            onClick={() => { setVariantIds(prev => [...prev, p.id]); setVariantSearch(''); }}
                            style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 12px', cursor:'pointer', borderBottom:'1px solid #f1f5f9', background:'#fff' }}
                            onMouseEnter={e => e.currentTarget.style.background='#f0fdf4'}
                            onMouseLeave={e => e.currentTarget.style.background='#fff'}
                          >
                            <img src={p.image_url||'https://placehold.co/32x32'} alt={p.name} style={{ width:28, height:28, objectFit:'contain', borderRadius:4, border:'1px solid #e2e8f0' }}/>
                            <span style={{ fontSize:'.8rem', fontWeight:600, flex:1 }}>{p.name}</span>
                            <span style={{ fontSize:'.75rem', color:'#94a3b8' }}>Rs {p.price?.toFixed(0)}</span>
                            <FiPlus size={13} color="#22c55e"/>
                          </div>
                        ))}
                      </div>
                    ) : <div style={{ fontSize:'.78rem', color:'var(--text-muted)', padding:'6px 0' }}>No products found</div>;
                  })()}
                  {/* Selected variants */}
                  {variantIds.length > 0 && (
                    <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                      {variantIds.map(vid => {
                        const vp = products.find(p => p.id === vid);
                        if (!vp) return null;
                        return (
                          <div key={vid} style={{ display:'flex', alignItems:'center', gap:10, padding:'6px 10px', background:'#f8fafc', border:'1.5px solid #e2e8f0', borderRadius:8 }}>
                            <img src={vp.image_url||'https://placehold.co/32x32'} alt={vp.name} style={{ width:28, height:28, objectFit:'contain', borderRadius:4, border:'1px solid #e2e8f0' }}/>
                            <span style={{ fontSize:'.8rem', fontWeight:600, flex:1 }}>{vp.name}</span>
                            <span style={{ fontSize:'.75rem', color:'#94a3b8' }}>Rs {vp.price?.toFixed(0)}</span>
                            <button onClick={() => setVariantIds(prev => prev.filter(i => i !== vid))} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:2, display:'flex' }}><FiX size={14}/></button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="admin-form-group">
                  <label>Status</label>
                  <div className="prod-modal-toggle">
                    <button type="button"
                      className={`prod-modal-toggle-btn ${form.is_active ? 'active' : ''}`}
                      onClick={() => setForm({...form, is_active: true})}>
                      <FiToggleRight size={15}/> Active
                    </button>
                    <button type="button"
                      className={`prod-modal-toggle-btn ${!form.is_active ? 'inactive' : ''}`}
                      onClick={() => setForm({...form, is_active: false})}>
                      <FiToggleLeft size={15}/> Inactive
                    </button>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-admin btn-admin-ghost" onClick={()=>setModal(false)}>Cancel</button>
                <button type="submit" className="btn-admin btn-admin-primary" disabled={saving}>{saving?'Saving…':editing?'Update':'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminProducts;
